import type { AspDomainVariants } from '@module/apis/client-side/preset/type';

export type Site = 'pandalive' | 'bktv';

export type SiteInfo = {
  name: string;
  host: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Site definitions — brand apex host + display name.
// ─────────────────────────────────────────────────────────────────────────────

export const siteInfo: Record<Site, SiteInfo> = {
  pandalive: { name: '팬더티비', host: 'pandalive.co.kr' },
  bktv: { name: 'BK티비', host: 'bktv.kr' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Local-dev prefixes — stripped during normalization so routing only sees the
// canonical host. Two forms exist:
//   - `local.bktv.kr`             dotted (KR / bktv / sales)
//   - `local-dev.pandalive.co.jp` hyphenated (fanza: `*.pandalive.co.jp` has a
//                                 single-level wildcard cert, so the env marker
//                                 glues onto the label; `local-dev` → `dev`).
// ─────────────────────────────────────────────────────────────────────────────

const NON_CANONICAL_PREFIXES = ['local.', 'local-'] as const;

const getNonCanonicalPrefix = (hostname: string): string => {
  for (const prefix of NON_CANONICAL_PREFIXES) {
    if (hostname.startsWith(prefix)) return prefix;
  }
  return '';
};

// ─────────────────────────────────────────────────────────────────────────────
// Core parsing
// ─────────────────────────────────────────────────────────────────────────────

export const extractHostname = (input: string): string => {
  if (!input || typeof input !== 'string') return '';

  try {
    if (input.includes('://')) {
      return new URL(input).hostname.toLowerCase();
    }
  } catch {
    // Fall back to loose hostname parsing for host[:port] inputs.
  }

  return input
    .toLowerCase()
    .replace(/^\[|\]$/g, '')
    .replace(/:\d+$/, '');
};

/** Bare lowercased host, local-dev prefix + port stripped. The single value sent to `/v1/config/preset`. */
export const normalizeHostname = (rawHost: string): string => {
  const extracted = extractHostname(rawHost);
  return extracted.slice(getNonCanonicalPrefix(extracted).length);
};

// ─────────────────────────────────────────────────────────────────────────────
// Brand / site
// ─────────────────────────────────────────────────────────────────────────────

const isBktvHostname = (rawHost: string): boolean => normalizeHostname(rawHost).includes('bktv');

/** True for pandalive-branded hosts (incl. fanza `pandalive.co.jp`); false for bktv. */
export const isPandaliveHostname = (rawHost: string): boolean => normalizeHostname(rawHost).includes('pandalive');

export const resolveSiteFromHostname = (rawHost: string): Site => (isBktvHostname(rawHost) ? 'bktv' : 'pandalive');

export const resolveSiteInfoFromHostname = (rawHost: string): SiteInfo => siteInfo[resolveSiteFromHostname(rawHost)];

// ─────────────────────────────────────────────────────────────────────────────
// Environment
// ─────────────────────────────────────────────────────────────────────────────

/** Not canonical production pandalive traffic: local-dev prefix, contains `dev`, or bktv (the internal dev brand). */
export const isNonProductionHostname = (rawHost: string): boolean => {
  const extracted = extractHostname(rawHost);
  return Boolean(getNonCanonicalPrefix(extracted)) || extracted.includes('dev') || isBktvHostname(extracted);
};

// ─────────────────────────────────────────────────────────────────────────────
// Mobile origin — maps the current host to its mobile counterpart, preserving the
// local-dev prefix. Used by the post-login cross-app (PC → mobile) redirect.
// (Whether the *current* page is mobile comes from `SettingConfig.isMoWeb`, not here.)
// ─────────────────────────────────────────────────────────────────────────────

export const resolveMobileOriginFromHostname = (rawHost: string, protocol = 'https'): string => {
  const extracted = extractHostname(rawHost);
  const envPrefix = getNonCanonicalPrefix(extracted);
  const normalized = extracted.slice(envPrefix.length);

  let host = 'm.pandalive.co.kr';
  if (normalized.includes('dev.pandalive.co.jp') || normalized.includes('dev-m.pandalive.co.jp')) {
    host = 'dev-m.pandalive.co.jp';
  } else if (normalized.includes('bktv')) {
    host = 'm.bktv.kr';
  }

  return `${protocol}://${envPrefix}${host}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// ASP (Application Service Provider) hostname classification
// ─────────────────────────────────────────────────────────────────────────────
//
// 우리 서비스가 ASP 로 노출되는 호스트는 두 가지로만 분류된다 (둘 다 `local.` prefix 변형 포함):
//   1. iframe : 제휴사 페이지가 <iframe> 으로 임베드 — wasp/masp.{pandalive.co.kr · bktv.kr}.
//   2. popup  : 제휴사 별 도메인을 새 창으로 — livetv.{aspCode} / mlivetv.{aspCode}.
// 부트스트랩·인증 분기가 완전히 다르다. 호스트가 늘면 이 두 정규식만 갱신한다 —
// aspConfigManager 와 미들웨어(proxy) 는 `classifyASPHostname` 만 호출한다.

const IFRAME_ASP_HOSTNAME_PATTERN = /^(?:local\.)?(?:wasp|masp)\.(?:pandalive\.co\.kr|bktv\.kr)$/;
const POPUP_ASP_HOSTNAME_PATTERN = /^(?:local\.)?(?:livetv|mlivetv)\.[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;

export type ASPServiceType = 'iframe' | 'popup' | 'none';

/**
 * 호스트의 ASP 서비스 유형을 단일 값으로 반환 — ASP 판정의 단일 진입점.
 * (iframe/popup 여부가 필요하면 이 반환값을 `=== 'iframe'` / `=== 'popup'` 으로 비교한다.)
 */
export const classifyASPHostname = (rawHost: string): ASPServiceType => {
  const host = extractHostname(rawHost);
  if (IFRAME_ASP_HOSTNAME_PATTERN.test(host)) return 'iframe';
  if (POPUP_ASP_HOSTNAME_PATTERN.test(host)) return 'popup';
  return 'none';
};

// ─────────────────────────────────────────────────────────────────────────────
// 제휴사 특화 — tple 모바일(mlivetv.tple.co.kr) 한정.
// tple 의 charge.php 는 deprecated `document.domain` + cross-subdomain `window.opener`
// 로 충전 완료를 처리한다. modern WebView(Chrome 115+, origin-keyed)에선 document.domain
// 이 no-op 이라 cross-subdomain opener 접근이 차단되어 결제완료 후 흰화면/멈춤이 발생한다.
// 모바일(mlivetv) 네이티브앱 WebView 케이스에서만 재현되어, 이 호스트에 한해 charge 를
// 새 창(window.open) 대신 self 이동(location.href)으로 열어 opener 관계를 same-origin 으로
// 만든다 (`goCharge` 참고). PC(livetv.tple.co.kr)는 미해당(부작용 최소화). 근본 해결은
// 제휴사측 charge.php 가 document.domain 의존을 버리고 same-origin/postMessage 로 가는 것.
// ─────────────────────────────────────────────────────────────────────────────

const TPLE_MOBILE_HOSTNAME_REGEX = /^mlivetv\.tple\.co\.kr$/;

export const isTpleMobileHostname = (rawHost: string): boolean =>
  TPLE_MOBILE_HOSTNAME_REGEX.test(normalizeHostname(rawHost));

// ─────────────────────────────────────────────────────────────────────────────
// ASP API origin (host-only) — site 별 인증/preset API origin.
// 일반 호스트는 site 로 판정하고, site 정보가 없는 popup 제휴사 호스트만 사내 전용
// `ooh.co.kr` 마커로 bktv 에 매핑한다 (실 파트너는 이 마커를 쓰지 않는다).
// ─────────────────────────────────────────────────────────────────────────────

const ASP_API_HOST: Record<Site, AspDomainVariants> = {
  pandalive: 'apiasp.pandalive.co.kr',
  bktv: 'apiasp.bktv.kr',
};

const BKTV_ASP_MARKER_REGEX = /(?:^|\.)ooh\.co\.kr$/;

export const resolveASPApiHost = (rawHost: string): AspDomainVariants => {
  const site: Site = BKTV_ASP_MARKER_REGEX.test(normalizeHostname(rawHost)) ? 'bktv' : resolveSiteFromHostname(rawHost);
  return ASP_API_HOST[site];
};
